import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { aurora, auroraPalette } from '@/components/aurora/tokens';

export interface MobileTopBarAction {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  badge?: number;
  disabled?: boolean;
}

interface MobileTopBarProps {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  backLabel?: string;
  /** Slot opzionale a sinistra del titolo (es. avatar). */
  leading?: ReactNode;
  /** Massimo 2 azioni a destra; eventuali ulteriori vengono troncate. */
  actions?: MobileTopBarAction[];
  className?: string;
  /**
   * Variante visiva: `glass` per Aurora glass + blur (default), `solid` se la
   * pagina ha già uno sfondo solido sotto e si vuole evitare il blur.
   */
  variant?: 'glass' | 'solid';
}

const MAX_ACTIONS = 2;

export default function MobileTopBar({
  title,
  subtitle,
  onBack,
  backLabel = 'Indietro',
  leading,
  actions,
  className,
  variant = 'glass',
}: MobileTopBarProps) {
  const visibleActions = (actions ?? []).slice(0, MAX_ACTIONS);
  return (
    <header
      className={cn(
        'sticky top-0 z-20 border-b border-border',
        variant === 'glass'
          ? 'bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70'
          : 'bg-background',
        className,
      )}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        backgroundImage:
          variant === 'glass'
            ? `linear-gradient(180deg, ${auroraPalette.slateSoft} 0%, transparent 100%)`
            : undefined,
      }}
      data-testid="mobile-topbar"
    >
      <div className="flex items-center gap-1 px-2 min-h-[56px]">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label={backLabel}
            className="h-11 w-11 inline-flex items-center justify-center rounded-full text-foreground/80 hover:bg-muted active:bg-muted/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            style={{ outlineColor: auroraPalette.indigo }}
            data-testid="mobile-topbar-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {leading && (
          <div className="shrink-0 flex items-center" data-testid="mobile-topbar-leading">
            {leading}
          </div>
        )}
        <div className="flex-1 min-w-0 px-1">
          <h1
            className={cn(aurora.type.title, 'truncate text-foreground')}
            data-testid="mobile-topbar-title"
          >
            {title}
          </h1>
          {subtitle && (
            <p
              className={cn(aurora.type.caption, 'truncate text-muted-foreground')}
              data-testid="mobile-topbar-subtitle"
            >
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-0.5" data-testid="mobile-topbar-actions">
          {visibleActions.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={a.onClick}
              disabled={a.disabled}
              aria-label={a.label}
              className="relative h-11 w-11 inline-flex items-center justify-center rounded-full text-foreground/80 hover:bg-muted active:bg-muted/80 disabled:opacity-40 transition-colors focus:outline-none focus-visible:ring-2"
              style={{ outlineColor: auroraPalette.indigo }}
              data-testid={`mobile-topbar-action-${a.id}`}
            >
              {a.icon}
              {typeof a.badge === 'number' && a.badge > 0 && (
                <span
                  aria-hidden
                  className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold inline-flex items-center justify-center ring-2 ring-background"
                >
                  {a.badge > 99 ? '99+' : a.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
